<script lang="ts">
  import { keyboard } from '../keys/keyboard.svelte';
  import { headerPublishLabel } from '../logic/inbox';
  import { subject, type PrStore } from '../state/pr.svelte';
  import { readiness } from '../logic/readiness';
  import { hasMember } from '../logic/attention';
  import { session } from '../state/session.svelte';
  import { nav } from '../nav.svelte';
  import {
    firstUnresolvedThread,
    headlineTarget,
    itemTarget,
    targetHref,
    targetName,
    type HeaderTarget,
  } from '../logic/headerTargets';
  import AttentionControls from './AttentionControls.svelte';
  import PreviewChip from './PreviewChip.svelte';
  import LabelList from './LabelList.svelte';

  /** Labels the header shows before "+N" (the Overview's Labels card lists them all). */
  const HEAD_LABELS = 6;
  import StackChip from './StackChip.svelte';
  import Popover from './Popover.svelte';
  import { changeHref, fileHref, type FileProgress } from '../logic/progressBreakdown';
  import { count } from '../logic/browse';
  import { prPath, type PrTab } from '../router';

  interface Props {
    pr: PrStore;
    compact?: boolean;
    /** `filtered`: counts only the changed files the explorer's file filter keeps. */
    /** Review progress over the comparison shown. */
    progress?: { done: number; total: number; filtered?: boolean } | null;
    /** The comparison's name when it is not the pull request's own (base → head): the meter then says its count is this comparison's. */
    comparison?: string | null;
    onpublish: () => void;
    /** The review meter (lines reviewed): show unreviewed files (Files view). */
    onunreviewed?: () => void;
    /** The review meter's breakdown by file (Files view), read when it opens. */
    breakdown?: () => { done: number; total: number; filtered: boolean; files: FileProgress[] };
    /** `a` goes to the Review view here (on Review it ticks a draft instead, so the key is not shown). */
    publishKey?: boolean;
    /** The view the page is on: the stack menu opens another pull request on the same one. */
    tab?: PrTab;
    /** Whether the stack menu is open (the page opens it by key). */
    stackOpen?: boolean;
  }
  let { pr, compact = false, progress = null, comparison = null, onpublish, onunreviewed, breakdown, publishKey = true, tab = 'overview', stackOpen = $bindable(false) }: Props = $props();
  const TO_PUBLISH = { kind: 'goto', place: 'review' } as const;

  // ---------------------------------------------------------------- the review meter's breakdown
  let meterButton: HTMLButtonElement | null = $state(null);
  let meterOpen = $state(false);
  /** The breakdown shown, read again as review marks land while it is open. */
  const detail = $derived(meterOpen && breakdown ? breakdown() : null);
  const filesPath = $derived(prPath(pr.owner, pr.repo, pr.number, 'files'));
  /** Unreviewed changes listed per file; the rest are a link to the file. */
  const CHANGES_SHOWN = 4;
  const percent = (done: number, total: number) => (total ? Math.floor((done / total) * 100) : 100);
  function closeMeter(refocus: boolean) {
    meterOpen = false;
    if (refocus) meterButton?.focus();
  }
  const changeName = (c: FileProgress['changes'][number]) =>
    `${c.title ? c.title + ' · ' : ''}${c.side === 'LEFT' ? 'L' : ''}${c.start === c.end ? c.start : `${c.start}–${c.end}`} · ${c.lines} line${c.lines === 1 ? '' : 's'}`;

  const data = $derived(pr.pr!);
  const ready = $derived(readiness(data, pr.unresolvedCount));
  const stateClass = $derived(data.state === 'MERGED' ? 'merged' : data.state === 'CLOSED' ? 'bad' : data.isDraft ? 'neutral' : '');
  const me = $derived(session.user?.login ?? pr.viewer);
  const inAttention = $derived(hasMember(pr.attention, me));
  // "<author> wants to merge <head> into <base>" while open; the merger may be someone else.
  const wording = $derived(
    data.state === 'MERGED'
      ? { verb: '’s branch', into: 'was merged into' }
      : data.state === 'CLOSED'
        ? { verb: ' proposed merging', into: 'into' }
        : { verb: ' wants to merge', into: 'into' },
  );
  const firstThread = $derived(firstUnresolvedThread(pr.threads, (pr.files ?? []).map((f) => f.filename)));
  const headTarget = $derived(headlineTarget(ready.status, firstThread));
  const threadTarget = $derived<HeaderTarget | null>(firstThread ? { kind: 'thread', thread: firstThread } : null);
  const href = (t: HeaderTarget) => targetHref(t, pr, nav.query) ?? undefined;
  const readOnlyTitle = $derived(pr.readOnlyText ?? undefined);
  const draftWord = $derived(pr.draftCount === 1 ? 'draft' : 'drafts');
  /** A publish in flight: the button says so until GitHub confirms it. */
  const publishing = $derived(pr.writes.on(subject.publish));
  /** The button to the Review view says what a publish sends now (see logic/inbox `headerPublishLabel`). */
  const primary = $derived(headerPublishLabel(pr.includedCount, pr.draftCount) ?? (pr.isAuthor ? 'Comment' : 'Review'));
  /** How the drafts split, for the counter's tooltip. */
  const draftsTitle = $derived(
    pr.draftCount
      ? `${pr.includedCount} included in your next review, ${pr.draftCount - pr.includedCount} not included${pr.heldCount ? `; ${pr.heldCount} for the conversation (lines outside the diff)` : ''}`
      : null,
  );
</script>

<header class="pr-head" class:ready={ready.ready} class:closed={data.state !== 'OPEN'}>
  <div class="pr-head-row">
    <div style="min-width:0;display:grid;gap:8px">
      <h1 class="pr-title" style:font-size={compact ? '22px' : undefined}>
        {data.title} <span class="num">#{data.number}</span>
      </h1>
      <div class="pr-sub">
        <span class="pill {stateClass}">{data.state === 'OPEN' ? (data.isDraft ? 'Draft' : 'Open') : data.state === 'MERGED' ? 'Merged' : 'Closed'}</span>
        <span><b>{data.author?.login ?? 'ghost'}</b>{wording.verb}</span>
        <span class="branch">{data.headRefName}</span><span>{wording.into}</span><span class="branch">{data.baseRefName}</span>
        <!-- Its labels, as GitHub has them (read-only here), after where it goes and before the chips that open things. -->
        <LabelList labels={data.labels} max={HEAD_LABELS} />
        {#if pr.previews.length}<PreviewChip previews={pr.previews} />{/if}
        <!-- Last in the line: it comes and goes with its own read, and moves nothing when it does. -->
        <StackChip stack={pr.stack} {tab} bind:open={stackOpen} expected={pr.stackExpected} onretry={() => pr.reloadStack()} />
      </div>
    </div>
    <div class="pr-actions">
      {#if data.state === 'OPEN'}
        {#if publishing}
          <!-- Not :disabled (that dims it below a readable contrast): it does nothing while publishing. -->
          <button class="btn primary" aria-busy="true" aria-disabled="true">
            <span class="spinner" aria-hidden="true"></span>{publishing.label}…
          </button>
        {:else}
          <button
            class="btn {ready.ready && !pr.draftCount ? 'go' : 'primary'}"
            onclick={onpublish}
            disabled={pr.readOnly}
            title={readOnlyTitle ?? `Publish your review${publishKey ? keyboard.hint(TO_PUBLISH) : ''}`}
          >
            {primary}{#if publishKey && keyboard.key(TO_PUBLISH)} <kbd style="color:inherit;border-color:currentColor;opacity:.7">{keyboard.key(TO_PUBLISH)}</kbd>{/if}
          </button>
        {/if}
      {/if}
    </div>
  </div>
  <div class="ready-line">
    {#if headTarget}
      <a class="headline go-to" href={href(headTarget)} aria-label={targetName(ready.headline, headTarget)}>{ready.headline}</a>
    {:else}
      <span class="headline">{ready.headline}</span>
    {/if}
    {#each ready.items as item (item.kind)}
      {@const t = itemTarget(item, ready.status, firstThread)}
      {#if t}
        <a class="pill chip {item.tone === 'good' ? '' : item.tone}" href={href(t)} aria-label={targetName(item.label, t)}>{item.label}</a>
      {:else}
        <span class="pill {item.tone === 'good' ? '' : item.tone}">{item.label}</span>
      {/if}
    {/each}
  </div>
  <div class="pr-head-row" style="align-items:center">
    <div class="attn" class:is-pending={!!pr.writes.on(subject.attention)} aria-busy={pr.writes.on(subject.attention) ? 'true' : undefined} aria-label="Attention set">
      <span class="eyebrow">Waiting on</span>
      <AttentionControls {pr} layout="inline" />
      {#if inAttention}<span class="pill pending">your turn</span>{/if}
    </div>
    <div class="meter">
      {#if progress}
        {@const figure = `${count(progress.done)}/${count(progress.total)}`}
        {@const here = comparison ? ` in this comparison (${comparison})` : ''}
        {@const label = `${figure} lines${progress.filtered ? ', filtered' : ' reviewed'}${here}`}
        {@const words = `lines${progress.filtered ? ' · filtered' : ' reviewed'}${comparison ? ' here' : ''}`}
        {@const filteredTitle = `Counts only the changed files the type and path filters keep${here}`}
        {#if breakdown}
          <button
            bind:this={meterButton}
            class="go-to"
            aria-haspopup="dialog"
            aria-expanded={meterOpen}
            aria-controls={meterOpen ? 'meter-breakdown' : undefined}
            aria-label="{label}: show the breakdown by file"
            title={progress.filtered ? filteredTitle : `Lines reviewed${here}, by file`}
            onclick={() => (meterOpen = !meterOpen)}
            ><b>{figure}</b>{words}<span class="caret" aria-hidden="true">▾</span></button
          >
        {:else if onunreviewed}
          <button
            class="go-to"
            onclick={onunreviewed}
            aria-label={targetName(label, { kind: 'unreviewed' })}
            title={progress.filtered ? filteredTitle : `Show unreviewed files${here}`}
            ><b>{figure}</b>{words}</button
          >
        {:else}
          <span title={progress.filtered ? filteredTitle : comparison ? `Lines reviewed${here}` : undefined}
            ><b>{figure}</b>{words}</span
          >
        {/if}
      {/if}
      {#if threadTarget}
        <a class="go-to bad" href={href(threadTarget)} aria-label={targetName(`${pr.unresolvedCount} unresolved`, threadTarget)}
          ><b>{pr.unresolvedCount}</b>unresolved</a
        >
      {:else}
        <span class:bad={pr.unresolvedCount > 0}><b>{pr.unresolvedCount}</b>unresolved</span>
      {/if}
      {#if data.state === 'OPEN' && !pr.readOnly}
        <button
          class="go-to"
          onclick={onpublish}
          aria-label={targetName(`${pr.draftCount} ${draftWord}`, { kind: 'publish' })}
          title={draftsTitle ?? 'Publish your review'}
          ><b>{pr.draftCount}</b>{draftWord}</button
        >
      {:else}
        <span title={draftsTitle ?? undefined}><b>{pr.draftCount}</b>{draftWord}</span>
      {/if}
    </div>
  </div>
</header>

{#if meterOpen && meterButton && detail}
  <Popover anchor={meterButton} role="dialog" label="Review progress by file" id="meter-breakdown" onclose={closeMeter}>
    <div class="mb">
      <div class="mb-head">
        <span><b>{count(detail.done)}</b> of <b>{count(detail.total)}</b> lines reviewed{comparison ? ` in this comparison (${comparison})` : ''} · {percent(detail.done, detail.total)}%{detail.filtered ? ' (filtered files)' : ''}</span>
        {#if onunreviewed && detail.done < detail.total}
          <button type="button" class="btn small" onclick={() => { closeMeter(false); onunreviewed?.(); }}>Show unreviewed files</button>
        {/if}
      </div>
      <ul class="mb-files">
        {#each detail.files as f (f.path)}
          {@const left = f.total - f.done}
          <li class:done={!left}>
            <a class="mb-file" href={fileHref(filesPath, f.path, nav.query)} onclick={() => closeMeter(false)} title={f.path}>
              <span class="mb-path">{f.path}</span>
              <span class="mb-count mono">{!left ? '✓ ' : ''}{f.done}/{f.total}{f.exact ? '' : '*'}</span>
              <span class="mb-bar" aria-hidden="true"><span style:width="{percent(f.done, f.total)}%"></span></span>
            </a>
            {#if f.changes.length}
              <ul class="mb-changes" aria-label="Unreviewed changes in {f.path}">
                {#each f.changes.slice(0, CHANGES_SHOWN) as c (c.block)}
                  <li><a href={changeHref(filesPath, f.path, c, nav.query)} onclick={() => closeMeter(false)}>{changeName(c)}</a></li>
                {/each}
                {#if f.changes.length > CHANGES_SHOWN}
                  <li class="muted">and {f.changes.length - CHANGES_SHOWN} more unreviewed change{f.changes.length - CHANGES_SHOWN === 1 ? '' : 's'}</li>
                {/if}
              </ul>
            {/if}
          </li>
        {:else}
          <li class="muted">No changed files{detail.filtered ? ' pass the filter' : ''}.</li>
        {/each}
      </ul>
      {#if detail.files.some((f) => !f.exact)}<p class="muted mb-note">* GitHub's count: the file's diff is not computed yet.</p>{/if}
    </div>
  </Popover>
{/if}

<style>
  .meter .caret {
    margin-left: 3px;
    font-size: 10px;
    opacity: 0.7;
  }
  .mb {
    width: min(520px, calc(100vw - 48px));
    display: grid;
    gap: 8px;
  }
  .mb-head {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 12px;
    align-items: center;
    justify-content: space-between;
    padding: 4px 6px 8px;
    border-bottom: 1px solid var(--line);
  }
  .mb-files,
  .mb-changes {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .mb-files {
    display: grid;
    gap: 2px;
  }
  .mb-file {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 2px 10px;
    align-items: baseline;
    padding: 5px 6px;
    border-radius: 6px;
    color: var(--fg);
    text-decoration: none;
  }
  .mb-file:hover,
  .mb-file:focus-visible {
    background: var(--soft);
  }
  .mb-path {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: ui-monospace, SFMono-Regular, Consolas, monospace;
    font-size: 12px;
  }
  .mb-count {
    color: var(--muted);
    font-size: 11px;
    font-variant-numeric: tabular-nums;
  }
  .done .mb-count {
    color: var(--good);
  }
  .mb-bar {
    grid-column: 1 / -1;
    height: 3px;
    border-radius: 2px;
    background: var(--line);
    overflow: hidden;
  }
  .mb-bar span {
    display: block;
    height: 100%;
    background: var(--review-color);
  }
  .mb-changes {
    display: grid;
    gap: 1px;
    margin: 0 0 4px 14px;
    font-size: 12px;
  }
  .mb-changes a {
    display: block;
    padding: 2px 6px;
    border-radius: 4px;
    text-decoration: none;
    overflow-wrap: anywhere;
  }
  .mb-changes a:hover,
  .mb-changes a:focus-visible {
    background: var(--soft);
  }
  .mb-changes .muted {
    padding: 2px 6px;
  }
  .mb-note {
    margin: 0;
    padding: 0 6px;
    font-size: 11px;
  }
  /* Header items that lead somewhere keep their look, with a quiet hover. */
  a.chip {
    text-decoration: none;
    transition: box-shadow 0.12s;
  }
  a.headline {
    text-decoration: none;
    color: var(--fg);
  }
  :global(.pr-head.ready) a.headline {
    color: var(--good);
  }
  a.chip:hover {
    box-shadow: inset 0 0 0 1px currentColor;
  }
  a.headline:hover {
    text-decoration: underline;
    text-underline-offset: 3px;
    text-decoration-thickness: 1px;
  }
  .meter .go-to {
    font: inherit;
    color: inherit;
    text-transform: inherit;
    letter-spacing: inherit;
    text-align: left;
    background: none;
    border: 0;
    padding: 0;
    text-decoration: none;
    border-radius: 4px;
  }
  .meter .go-to:hover {
    color: var(--accent);
  }
  .meter .go-to:hover :global(b) {
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 3px;
  }
</style>
